import React, { useEffect, useState } from 'react';
import styles from '../../app.module.css';

const SecuritySection = ({ securitySchemes: parentSecuritySchemes, handleSecuritySchemeChange }) => {
  const [securitySchemes, setSecuritySchemes] = useState([]);
  const [availableTypes, setAvailableTypes] = useState(['apiKey', 'http', 'oauth2', 'openIdConnect']);
  const [newScheme, setNewScheme] = useState(null);
  const [editingScheme, setEditingScheme] = useState(null);
  const [editingSchemeIndex, setEditingSchemeIndex] = useState(null);
  const [showScopeFields, setShowScopeFields] = useState(false);
  const bearerFormatOptions = ['JWT', 'OAuth', 'Custom'];
  const oauthFlows = ['implicit', 'authorizationCode', 'clientCredentials', 'password'];
  const [errors, setErrors] = useState({});
  const [popupMessage, setPopupMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  const showPopupModal = (message) => {
    setPopupMessage(message);
    setShowPopup(true);
  };

  useEffect(() => {
    const schemesArray = Object.keys(parentSecuritySchemes || {}).map((name) => ({
      name,
      ...parentSecuritySchemes[name],
    }));
    setSecuritySchemes(schemesArray);

    const usedTypes = schemesArray.map((scheme) => scheme.type);
    setAvailableTypes(['apiKey', 'http', 'oauth2', 'openIdConnect'].filter((type) => !usedTypes.includes(type)));
  }, [parentSecuritySchemes]);

  const validateUrl = (field, value) => {
    const urlPattern = /^(https:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
    const isValid = urlPattern.test(value);
    setErrors((prev) => ({
      ...prev,
      [field]: isValid ? null : 'Invalid URL format. Ensure it starts with http:// or https:// and has a valid domain.',
    }));
    return isValid;
  };

  const handleInputChange = (field, value) => {
    if (['openIdConnectUrl', 'authorizationUrl', 'tokenUrl'].includes(field)) {
      validateUrl(field, value);
    }
    setNewScheme((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditInputChange = (field, value) => {
    if (field === 'flow') {
      const requiredForFlow = {
        implicit: ['authorizationUrl', 'refreshUrl', 'scopes'],
        authorizationCode: ['authorizationUrl', 'tokenUrl', 'refreshUrl', 'scopes'],
        clientCredentials: ['tokenUrl', 'refreshUrl', 'scopes'],
        password: ['tokenUrl', 'refreshUrl', 'scopes'],
      };

      setEditingScheme((prev) => {
        const newState = { ...prev, flow: value };
        const required = requiredForFlow[value] || [];

        if (!required.includes('authorizationUrl') && newState.authorizationUrl) {
          delete newState.authorizationUrl;
        }
        if (!required.includes('tokenUrl') && newState.tokenUrl) {
          delete newState.tokenUrl;
        }
        return newState;
      });
    } else {
      if (['openIdConnectUrl', 'authorizationUrl', 'tokenUrl', 'refreshUrl'].includes(field)) {
        validateUrl(field, value);
      }
      setEditingScheme((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleAddNewScheme = () => {
    if (!newScheme && availableTypes.length > 0) {
      setNewScheme({
        type: '',
        name: '',
        scheme: '',
        in: '',
        bearerFormat: '',
        openIdConnectUrl: '',
        flow: '',
        authorizationUrl: '',
        tokenUrl: '',
        refreshUrl: '',
        scopes: {},
        availableScopes: ['read', 'write'],
        selectedScope: '',
        scopeDescription: '',
      });
    }
  };

  const handleSaveScheme = () => {
    if (!newScheme?.type || !newScheme?.name) {
      showPopupModal('Please provide both a type and a name for the security scheme.');
      return;
    }

    const hasErrors = Object.values(errors).some((error) => error);
    if (hasErrors) {
      showPopupModal('Please fix the errors before saving.');
      return;
    }

    const nameExists = securitySchemes.some((scheme) => scheme.name === newScheme.name);
    if (nameExists) {
      showPopupModal(`The name "${newScheme.name}" is already in use. Please choose a different name.`);
      return;
    }

    let updatedScheme;

    if (newScheme.type === 'http') {
      if (!newScheme.scheme) {
        showPopupModal('Please select an HTTP scheme.');
        return;
      }

      updatedScheme = {
        type: 'http',
        scheme: newScheme.scheme,
      };

      if (newScheme.scheme === 'bearer') {
        if (!newScheme.bearerFormat) {
          showPopupModal('Please select a Bearer format.');
          return;
        }
        updatedScheme.bearerFormat = newScheme.bearerFormat;
      }
    } else if (newScheme.type === 'apiKey') {
      if (!newScheme.in) {
        showPopupModal('Please provide the location for the API key.');
        return;
      }
      updatedScheme = {
        type: 'apiKey',
        name: newScheme.name,
        in: newScheme.in,
      };
    } else if (newScheme.type === 'openIdConnect') {
      if (!newScheme.openIdConnectUrl) {
        showPopupModal('Please provide the OpenID Connect URL.');
        return;
      }
      updatedScheme = {
        type: 'openIdConnect',
        openIdConnectUrl: newScheme.openIdConnectUrl,
      };
    } else if (newScheme.type === 'oauth2') {
      if (!newScheme.flow) {
        showPopupModal('Please select an OAuth2 flow.');
        return;
      }
      const flow = newScheme.flow;
      const requiredFields = {
        implicit: ['authorizationUrl', 'refreshUrl', 'scopes'],
        authorizationCode: ['authorizationUrl', 'tokenUrl', 'refreshUrl', 'scopes'],
        clientCredentials: ['tokenUrl', 'refreshUrl', 'scopes'],
        password: ['tokenUrl', 'refreshUrl', 'scopes'],
      };

      const missingFields = requiredFields[flow].filter((field) => !newScheme[field]);
      if (missingFields.length > 0) {
        showPopupModal(`Please provide the following fields: ${missingFields.join(', ')}`);
        return;
      }

      updatedScheme = {
        type: 'oauth2',
        flows: {
          [flow]: {
            ...(newScheme.authorizationUrl && { authorizationUrl: newScheme.authorizationUrl }),
            ...(newScheme.tokenUrl && { tokenUrl: newScheme.tokenUrl }),
            ...(newScheme.refreshUrl && { refreshUrl: newScheme.refreshUrl }),
            ...(newScheme.scopes && { scopes: newScheme.scopes }),
          },
        },
      };
    } else {
      showPopupModal('Unsupported security scheme type.');
      return;
    }

    handleSecuritySchemeChange(newScheme.name, updatedScheme);
    setSecuritySchemes((prev) => [...prev, { name: newScheme.name, ...updatedScheme }]);
    setAvailableTypes((prev) => prev.filter((type) => type !== newScheme.type));
    setNewScheme(null);
    setShowScopeFields(false);
  };

  const handleRemoveScheme = (index) => {
    const removedScheme = securitySchemes[index];
    if (!removedScheme) return;

    handleSecuritySchemeChange(removedScheme.name, undefined);
    const updatedSchemes = securitySchemes.filter((_, i) => i !== index);
    setSecuritySchemes(updatedSchemes);
    setAvailableTypes((prev) => [...prev, removedScheme.type]);
  };

  const handleUpdateScheme = (index) => {
    let updatedScheme = {};

    if (editingScheme.type === 'http') {
      if (!editingScheme.scheme) {
        showPopupModal('Please select an HTTP scheme.');
        return;
      }
      updatedScheme = {
        type: 'http',
        scheme: editingScheme.scheme,
      };
      if (editingScheme.scheme === 'bearer') {
        if (!editingScheme.bearerFormat) {
          showPopupModal('Please select a Bearer format.');
          return;
        }
        updatedScheme.bearerFormat = editingScheme.bearerFormat;
      }
    } else if (editingScheme.type === 'apiKey') {
      if (!editingScheme.in) {
        showPopupModal('Please provide the location for the API key.');
        return;
      }
      updatedScheme = {
        type: 'apiKey',
        name: securitySchemes[index].name,
        in: editingScheme.in,
      };
    } else if (editingScheme.type === 'openIdConnect') {
      if (!editingScheme.openIdConnectUrl) {
        showPopupModal('Please provide the OpenID Connect URL.');
        return;
      }
      updatedScheme = {
        type: 'openIdConnect',
        openIdConnectUrl: editingScheme.openIdConnectUrl,
      };
    } else if (editingScheme.type === 'oauth2') {
      if (!editingScheme.flow) {
        showPopupModal('Please select an OAuth2 flow.');
        return;
      }
      const flow = editingScheme.flow;
      const requiredFields = {
        implicit: ['authorizationUrl', 'refreshUrl', 'scopes'],
        authorizationCode: ['authorizationUrl', 'tokenUrl', 'refreshUrl', 'scopes'],
        clientCredentials: ['tokenUrl', 'refreshUrl', 'scopes'],
        password: ['tokenUrl', 'refreshUrl', 'scopes'],
      };
      const missingFields = requiredFields[flow].filter((field) => !editingScheme[field]);
      if (missingFields.length > 0) {
        showPopupModal(`Please provide the following fields: ${missingFields.join(', ')}`);
        return;
      }
      updatedScheme = {
        type: 'oauth2',
        flows: {
          [flow]: {
            ...(editingScheme.authorizationUrl && { authorizationUrl: editingScheme.authorizationUrl }),
            ...(editingScheme.tokenUrl && { tokenUrl: editingScheme.tokenUrl }),
            ...(editingScheme.refreshUrl && { refreshUrl: editingScheme.refreshUrl }),
            ...(editingScheme.scopes && { scopes: editingScheme.scopes }),
          },
        },
      };
    } else {
      showPopupModal('Unsupported security scheme type.');
      return;
    }


    const updatedSchemes = [...securitySchemes];
    updatedSchemes[index] = { name: securitySchemes[index].name, ...updatedScheme };
    setSecuritySchemes(updatedSchemes);
    handleSecuritySchemeChange(securitySchemes[index].name, updatedScheme);
    setEditingSchemeIndex(null);
    setEditingScheme(null);
  };

  return (
    <div>
      <h3 className={styles.centeredHeading}>Security Schemes</h3>

      {securitySchemes.map((scheme, index) => (
        <div
          key={index}
          className={styles.mainContainer}
        >
          {editingSchemeIndex === index ? (
            <div>
              <p>
                <strong>Name:</strong> {scheme.name}
              </p>
              <p>
                <strong>Type:</strong> {scheme.type}
              </p>

              {editingScheme.type === 'http' && (
                <>
                  <div>
                    <label>HTTP Scheme:</label>
                    <select
                      value={editingScheme.scheme || ''}
                      onChange={(e) => handleEditInputChange('scheme', e.target.value)}
                      className={styles.style1}
                    >
                      <option value="">Select HTTP Scheme</option>
                      <option value="basic">Basic Auth</option>
                      <option value="bearer">Bearer</option>
                      <option value="mutualTLS">Mutual TLS</option>
                    </select>
                  </div>
                  {editingScheme.scheme === 'bearer' && (
                    <div>
                      <label>Bearer Format:</label>
                      <select
                        value={editingScheme.bearerFormat || ''}
                        onChange={(e) => handleEditInputChange('bearerFormat', e.target.value)}
                        className={styles.style1}
                      >
                        <option value="">Select Bearer Format</option>
                        {bearerFormatOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              )}

              {editingScheme.type === 'apiKey' && (
                <div>
                  <label>In:</label>
                  <select
                    value={editingScheme.in || ''}
                    onChange={(e) => handleEditInputChange('in', e.target.value)}
                    className={styles.style1}
                  >
                    <option value="">Select Location</option>
                    <option value="header">Header</option>
                    <option value="query">Query</option>
                    <option value="cookie">Cookie</option>
                  </select>
                </div>
              )}

              {editingScheme.type === 'openIdConnect' && (
                <div>
                  <label>OpenID Connect URL:</label>
                  <input
                    type="text"
                    value={editingScheme.openIdConnectUrl || ''}
                    onChange={(e) => handleEditInputChange('openIdConnectUrl', e.target.value)}
                    placeholder="Enter OpenID Connect URL"
                    className={styles.style1}
                  />
                </div>
              )}

              {editingScheme.type === 'oauth2' && (
                <>
                  <div>
                    <label>Flow:</label>
                    <select
                      value={editingScheme.flow || ''}
                      onChange={(e) => handleEditInputChange('flow', e.target.value)}
                      className={styles.style1}
                    >
                      <option value="">Select Flow</option>
                      {oauthFlows.map((flow) => (
                        <option key={flow} value={flow}>
                          {flow}
                        </option>
                      ))}
                    </select>
                  </div>

                  {(['implicit', 'authorizationCode'].includes(editingScheme.flow)) && (
                    <div>
                      <label>Authorization URL:</label>
                      <input
                        type="text"
                        value={editingScheme.authorizationUrl || ''}
                        onChange={(e) => handleEditInputChange('authorizationUrl', e.target.value)}
                        placeholder="Enter Authorization URL"
                        className={styles.style1}
                      />
                    </div>
                  )}

                  {(['authorizationCode', 'clientCredentials', 'password'].includes(editingScheme.flow)) && (
                    <div>
                      <label>Token URL:</label>
                      <input
                        type="text"
                        value={editingScheme.tokenUrl || ''}
                        onChange={(e) => handleEditInputChange('tokenUrl', e.target.value)}
                        placeholder="Enter Token URL"
                        className={styles.style1}
                      />
                    </div>
                  )}

                  {(['implicit', 'authorizationCode', 'clientCredentials', 'password'].includes(editingScheme.flow)) && (
                    <div>
                      <label>Refresh URL:</label>
                      <input
                        type="text"
                        value={editingScheme.refreshUrl || ''}
                        onChange={(e) => handleEditInputChange('refreshUrl', e.target.value)}
                        placeholder="Enter Refresh URL"
                        className={styles.style1}
                      />
                    </div>
                  )}

                  <div>
                    <label>Scopes:</label>
                    {editingScheme.scopes &&
                      Object.entries(editingScheme.scopes).map(([scope, description], idx) => (
                        <div key={idx} className={styles.style5}>
                          <span className={styles.labelStyle}>{scope}:</span>
                          <input
                            type="text"
                            value={description}
                            onChange={(e) => {
                              const newScopes = { ...editingScheme.scopes, [scope]: e.target.value };
                              setEditingScheme((prev) => ({ ...prev, scopes: newScopes }));
                            }}
                            placeholder="Enter description"
                            className={styles.style1}
                          />
                        </div>
                      ))}
                  </div>
                </>
              )}

              <div className={styles.sNcButtonContainer}>
                <button
                  className={styles.saveButton}
                  onClick={() => handleUpdateScheme(index)}
                >
                  Save
                </button>
                <button
                  className={styles.cancelButton}
                  onClick={() => {
                    setEditingSchemeIndex(null);
                    setEditingScheme(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p>
                <strong>Name:</strong> {scheme.name}
              </p>
              <p>
                <strong>Type:</strong> {scheme.type}
              </p>
              {scheme.type === 'http' && scheme.scheme && (
                <p>
                  <strong>Scheme:</strong> {scheme.scheme}
                </p>
              )}
              {scheme.type === 'http' && scheme.scheme === 'bearer' && scheme.bearerFormat && (
                <p>
                  <strong>Bearer Format:</strong> {scheme.bearerFormat}
                </p>
              )}
              {scheme.type === 'apiKey' && scheme.in && (
                <p>
                  <strong>In:</strong> {scheme.in}
                </p>
              )}
              {scheme.type === 'openIdConnect' && scheme.openIdConnectUrl && (
                <p>
                  <strong>OpenID Connect URL:</strong> {scheme.openIdConnectUrl}
                </p>
              )}
              {scheme.type === 'oauth2' && scheme.flows && (
                <div>
                  <p>
                    <strong>OAuth2 Flow:</strong> {Object.keys(scheme.flows).join(', ')}
                  </p>
                  {Object.entries(scheme.flows).map(([flow, details]) => (
                    <div key={flow} className={styles.securityFlows}>
                      {details.authorizationUrl && (
                        <p>
                          <strong>Authorization URL:</strong> {details.authorizationUrl}
                        </p>
                      )}
                      {details.tokenUrl && (
                        <p>
                          <strong>Token URL:</strong> {details.tokenUrl}
                        </p>
                      )}
                      {details.refreshUrl && (
                        <p>
                          <strong>Refresh URL:</strong> {details.refreshUrl}
                        </p>
                      )}
                      {details.scopes && (
                        <div>
                          <strong>Scopes:</strong>
                          <ul>
                            {Object.entries(details.scopes).map(([scope, description]) => (
                              <li key={scope}>
                                {scope}: {description}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div
                className={styles.actionButtonsContainer}
              >
                <button
                  className={styles.editButton}
                  onClick={() => {
                    setEditingSchemeIndex(index);
                    setEditingScheme({
                      ...scheme,
                      ...(scheme.type === 'oauth2' && scheme.flows
                        ? scheme.flows[Object.keys(scheme.flows)[0]]
                        : {}),
                    });
                  }}
                >
                  Edit
                </button>
                <button
                  className={styles.deleteButton}
                  onClick={() => handleRemoveScheme(index)}
                >
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {/* New Scheme Form */}
      {newScheme && (
        <div
          className={styles.mainContainer}
        >
          <div>
            <label>Security Type:</label>
            <select
              value={newScheme.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className={styles.style1}
            >
              <option value="">Select Type</option>
              {availableTypes.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Name:</label>
            <input
              type="text"
              value={newScheme.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Enter Name"
              className={styles.style1}
            />
          </div>

          {newScheme.type === 'http' && (
            <>
              <div>
                <label>HTTP Scheme:</label>
                <select
                  value={newScheme.scheme}
                  onChange={(e) => handleInputChange('scheme', e.target.value)}
                  className={styles.style1}
                >
                  <option value="">Select HTTP Scheme</option>
                  <option value="basic">Basic Auth</option>
                  <option value="bearer">Bearer</option>
                  <option value="mutualTLS">Mutual TLS</option>
                </select>
              </div>

              {newScheme.scheme === 'bearer' && (
                <div>
                  <label>Bearer Format:</label>
                  <select
                    value={newScheme.bearerFormat}
                    onChange={(e) => handleInputChange('bearerFormat', e.target.value)}
                    className={styles.style1}
                  >
                    <option value="">Select Bearer Format</option>
                    {bearerFormatOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </>
          )}

          {newScheme.type === 'apiKey' && (
            <div>
              <label>In:</label>
              <select
                value={newScheme.in}
                onChange={(e) => handleInputChange('in', e.target.value)}
                className={styles.style1}
              >
                <option value="">Select Location</option>
                <option value="header">Header</option>
                <option value="query">Query</option>
                <option value="cookie">Cookie</option>
              </select>
            </div>
          )}

          {newScheme.type === 'openIdConnect' && (
            <div>
              <label>OpenID Connect URL:</label>
              <input
                type="text"
                value={newScheme.openIdConnectUrl}
                onChange={(e) => handleInputChange('openIdConnectUrl', e.target.value)}
                placeholder="Enter OpenID Connect URL"
                className={styles.style1}
              />
              {errors.openIdConnectUrl && (
                <p className={styles.inlineAlerts}>{errors.openIdConnectUrl}</p>
              )}
            </div>
          )}
          {newScheme.type === 'oauth2' && (
            <>
              <div>
                <label>Flow:</label>
                <select
                  value={newScheme.flow}
                  onChange={(e) => handleInputChange('flow', e.target.value)}
                  className={styles.style1}
                >
                  <option value="">Select Flow</option>
                  {oauthFlows.map((flow) => (
                    <option key={flow} value={flow}>
                      {flow}
                    </option>
                  ))}
                </select>
              </div>

              {['implicit', 'authorizationCode'].includes(newScheme.flow) && (
                <div>
                  <label>Authorization URL:</label>
                  <input
                    type="text"
                    value={newScheme.authorizationUrl}
                    onChange={(e) => handleInputChange('authorizationUrl', e.target.value)}
                    placeholder="Enter Authorization URL"
                    className={styles.style1}
                  />
                  {errors.authorizationUrl && (
                    <p className={styles.inlineAlerts}>{errors.authorizationUrl}</p>
                  )}
                </div>
              )}

              {['authorizationCode', 'clientCredentials', 'password'].includes(newScheme.flow) && (
                <div>
                  <label>Token URL:</label>
                  <input
                    type="text"
                    value={newScheme.tokenUrl}
                    onChange={(e) => handleInputChange('tokenUrl', e.target.value)}
                    placeholder="Enter Token URL"
                    className={styles.style1}
                  />
                  {errors.tokenUrl && (
                    <p className={styles.inlineAlerts}>{errors.tokenUrl}</p>
                  )}
                </div>
              )}

              {['implicit', 'authorizationCode', 'clientCredentials', 'password'].includes(newScheme.flow) && (
                <div>
                  <label>Refresh URL:</label>
                  <input
                    type="text"
                    value={newScheme.refreshUrl}
                    onChange={(e) => handleInputChange('refreshUrl', e.target.value)}
                    placeholder="Enter Refresh URL"
                    className={styles.style1}
                  />
                </div>
              )}

              <div>
                <label>Scopes:</label>
                {newScheme.scopes &&
                  Object.entries(newScheme.scopes).map(([scope, description], index) => (
                    <div
                      key={index}
                      className={styles.style5}
                    >
                      <span className={styles.securityScopeName}>
                        {scope}:
                      </span>
                      <span className={styles.securityScopeDesc}>{description}</span>
                    </div>
                  ))}

                {!showScopeFields && (
                  <button
                    onClick={() => setShowScopeFields(true)}
                    className={`${styles.propertyButton} ${newScheme.availableScopes.length === 0 ? styles.disabledButton : ''}`}
                    disabled={newScheme.availableScopes.length === 0}
                  >
                    Add Scopes
                  </button>
                )}

                {showScopeFields && (
                  <div className={styles.style4}>
                    <div className={styles.style5}>
                      <select
                        value={newScheme.selectedScope || ''}
                        onChange={(e) => handleInputChange('selectedScope', e.target.value)}
                        className={styles.securityScopeSelect}
                      >
                        <option value="">Select Scope</option>
                        {newScheme.availableScopes.map((scope) => (
                          <option key={scope} value={scope}>
                            {scope}
                          </option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Enter description"
                        value={newScheme.scopeDescription || ''}
                        onChange={(e) => handleInputChange('scopeDescription', e.target.value)}
                        className={styles.securityScopeInput}
                      />
                      <button
                        className={styles.securityAddScopeFieldButton}
                        onClick={() => {
                          if (!newScheme.selectedScope || !newScheme.scopeDescription) {
                            showPopupModal('Please select a scope and provide a description.');
                            return;
                          }
                          const updatedScopes = {
                            ...newScheme.scopes,
                            [newScheme.selectedScope]: newScheme.scopeDescription,
                          };
                          const remainingScopes = newScheme.availableScopes.filter(
                            (scope) => scope !== newScheme.selectedScope
                          );

                          setNewScheme((prev) => ({
                            ...prev,
                            scopes: updatedScopes,
                            availableScopes: remainingScopes,
                            selectedScope: '',
                            scopeDescription: '',
                          }));
                          if (remainingScopes.length === 0) {
                            setShowScopeFields(false);
                          }
                        }}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                )}

                {newScheme.availableScopes.length === 0 && (
                  <p className={styles.securityAllScopesDefined}>
                    All scopes have been defined.
                  </p>
                )}
              </div>
            </>
          )}
          <div className={styles.sNcButtonContainer}>
            <button
              className={styles.saveButton}
              onClick={handleSaveScheme}
            >
              Save
            </button>
            <button
              className={styles.cancelButton}
              onClick={() => {
                setNewScheme(null);
                setShowScopeFields(false);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div>
        <button
          className={`${styles.addNewButton} ${availableTypes.length === 0 ? styles.disabledButton : ''}`}
          onClick={handleAddNewScheme}
          disabled={availableTypes.length === 0}
        >
          + Add Security
        </button>
      </div>
      {showPopup && (
        <div className={styles.popupOverlay}>
          <div className={styles.popup}>
            <h3>Error</h3>
            <p>{popupMessage}</p>
            <button onClick={() => setShowPopup(false)} className="popup-close-button">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecuritySection;
